import React, { useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import TechStackFormField from 'pages/contract/components/TechStackFormField';
import TemplateFormField, { TemplateSelectItem } from 'pages/contract/components/TemplateFormField';

// --- 型定義 ---

export type TemplateGroupItem = {
  /** 項目キー */
  itemKey: string;
  /** 表示ラベル */
  label: string;
  /** フォーム種別 */
  formType: string;
  /** 選択肢リスト */
  selectItems?: TemplateSelectItem[];
};

export type TemplateGroup = {
  /** グループID */
  groupId: string;
  /** グループ名 */
  name: string;
  /** グループ内アイテム（sortOrder順） */
  items: TemplateGroupItem[];
};

type TemplateGroupViewProps = {
  /** 表示するグループ一覧 */
  groups: TemplateGroup[];
  /** 表示モード: edit=フォーム入力, preview=読み取り専用 */
  mode: 'edit' | 'preview';
  /** 各アイテムの値（key: itemKey, value: 入力値） */
  values: Record<string, string>;
  /** 値変更ハンドラ（editモード時） */
  onValueChange: (itemKey: string, value: string) => void;
  /** アイテムが無効（disabled）かどうか */
  disabled?: boolean;
  /** ラベル部分の最小幅 (デフォルト: 140px) */
  labelMinWidth?: number;
  /** アコーディオンのデフォルト展開状態 (デフォルト: true) */
  defaultExpanded?: boolean;
  /** 技術スタック選択中IDリスト（FIXED_MEMBER_SKILL用） */
  techStackIds?: string[];
  /** 技術スタック変更ハンドラ（FIXED_MEMBER_SKILL用） */
  onTechStackChange?: (ids: string[]) => void;
  /** 差分表示用の値（previewモードで差分がある場合に緑文字で表示） */
  diffValues?: Record<string, string>;
};

/**
 * テンプレートグループ表示コンポーネント。
 *
 * 契約テンプレート設定画面の編集/プレビュータブ、
 * 契約申請画面のテンプレート項目表示で共通利用する。
 *
 * 各グループはアコーディオン形式で表示し、
 * 内部のアイテムは「ラベル: フォーム」のレイアウトで表示する。
 */
const TemplateGroupView: React.FC<TemplateGroupViewProps> = ({
  groups,
  mode,
  values,
  onValueChange,
  disabled = false,
  labelMinWidth = 140,
  defaultExpanded = true,
  techStackIds = [],
  onTechStackChange,
  diffValues,
}) => {
  if (groups.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        表示するテンプレート項目がありません
      </Typography>
    );
  }

  return (
    <Stack spacing={1}>
      {groups.map((group) =>
        group.groupId === 'FIXED_MEMBER_SKILL' ? (
          <TechStackFormField
            key={group.groupId}
            groupName={group.name}
            mode={mode}
            selectedIds={techStackIds}
            onChange={onTechStackChange || (() => {})}
            disabled={disabled}
            labelMinWidth={labelMinWidth}
            defaultExpanded={defaultExpanded}
          />
        ) : (
          <TemplateAccordionGroup
            key={group.groupId}
            group={group}
            mode={mode}
            values={values}
            onValueChange={onValueChange}
            disabled={disabled}
            labelMinWidth={labelMinWidth}
            defaultExpanded={defaultExpanded}
            diffValues={diffValues}
          />
        ),
      )}
    </Stack>
  );
};

// --- TemplateItemRow ---
type TemplateItemRowProps = {
  item: TemplateGroupItem;
  mode: 'edit' | 'preview';
  value: string;
  onValueChange: (value: string) => void;
  disabled: boolean;
  labelMinWidth: number;
  hasDiff?: boolean;
};

const TemplateItemRow: React.FC<TemplateItemRowProps> = ({
  item,
  mode,
  value,
  onValueChange,
  disabled,
  labelMinWidth,
  hasDiff = false,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
      }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          minWidth: labelMinWidth,
          maxWidth: labelMinWidth,
          pt: 1,
          wordBreak: 'break-word',
          flexShrink: 0,
        }}
      >
        {item.label}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {mode === 'edit' ? (
          <TemplateFormField
            formType={item.formType}
            label={item.label}
            value={value}
            onChange={onValueChange}
            selectItems={item.selectItems}
            disabled={disabled}
          />
        ) : (
          <Typography
            variant="body2"
            color={value ? (hasDiff ? 'success.main' : 'text.primary') : 'text.secondary'}
            sx={{ pt: 1, fontWeight: hasDiff ? 600 : undefined }}
          >
            {formatPreviewValue(item.formType, value, item.selectItems || [])}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// --- Preview value formatter ---
const formatPreviewValue = (
  formType: string,
  value: string,
  selectItems: TemplateSelectItem[],
): string => {
  if (!value || value === '[]') return '—';

  switch (formType) {
    case 'SELECT':
    case 'RADIO': {
      const found = selectItems.find((si) => si.code === value);
      return found ? found.name : value;
    }
    case 'MULTI_SELECT':
    case 'MULTI_CHECK':
    case 'MULTI_CHECK_BUTTON': {
      try {
        const arr: string[] = JSON.parse(value);
        return (
          arr
            .map((code) => {
              const found = selectItems.find((si) => si.code === code);
              return found ? found.name : code;
            })
            .join(', ') || '—'
        );
      } catch {
        return value;
      }
    }
    case 'CHECK':
      return value === 'true' ? 'あり' : 'なし';
    case 'TECH_STACK_SELECT': {
      try {
        const arr: string[] = JSON.parse(value);
        return arr.join(', ') || '—';
      } catch {
        return value;
      }
    }
    case 'DATE_PICKER_START':
    case 'DATE_PICKER_END':
      return value ? value.substring(0, 10).replace(/-/g, '/') : '—';
    default:
      return value || '—';
  }
};

// --- TemplateAccordionGroup ---
type TemplateAccordionGroupProps = {
  group: TemplateGroup;
  mode: 'edit' | 'preview';
  values: Record<string, string>;
  onValueChange: (itemKey: string, value: string) => void;
  disabled: boolean;
  labelMinWidth: number;
  defaultExpanded: boolean;
  diffValues?: Record<string, string>;
};

const TemplateAccordionGroup: React.FC<TemplateAccordionGroupProps> = ({
  group,
  mode,
  values,
  onValueChange,
  disabled,
  labelMinWidth,
  defaultExpanded,
  diffValues,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const handleToggle = () => setExpanded((prev) => !prev);

  return (
    <Accordion
      elevation={0}
      expanded={expanded}
      sx={{
        '&:before': { display: 'none' },
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <AccordionSummary
        expandIcon={
          <IconButton size="small" onClick={handleToggle} sx={{ cursor: 'pointer' }}>
            <ExpandMoreIcon />
          </IconButton>
        }
        sx={{
          px: 2,
          minHeight: 40,
          flexDirection: 'row-reverse',
          gap: 1,
          cursor: 'default',
          '&.Mui-focusVisible': { bgcolor: 'transparent' },
          '& .MuiAccordionSummary-expandIconWrapper': { transform: 'none' },
          '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': { transform: 'none' },
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Typography
          variant="body2"
          fontWeight={600}
          onClick={handleToggle}
          sx={{ cursor: 'pointer' }}
        >
          {group.name}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
        <Stack spacing={2}>
          {group.items.map((item) => {
            const currentVal = values[item.itemKey] || '';
            const diffVal = diffValues?.[item.itemKey];
            // 空値の正規化: "" と "[]" は同等に扱う
            const isCurrentEmpty = !currentVal || currentVal === '[]';
            const isDiffEmpty = !diffVal || diffVal === '[]';
            // 差分判定: 両方空なら差分なし、それ以外は文字列比較
            const hasDiff =
              diffVal != null && !(isCurrentEmpty && isDiffEmpty) && diffVal !== currentVal;
            return (
              <TemplateItemRow
                key={item.itemKey}
                item={item}
                mode={mode}
                value={currentVal}
                onValueChange={(v) => onValueChange(item.itemKey, v)}
                disabled={disabled}
                labelMinWidth={labelMinWidth}
                hasDiff={hasDiff}
              />
            );
          })}
          {group.items.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              項目がありません
            </Typography>
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

export default TemplateGroupView;
